import { trace } from 'owa-trace';

/**
 * We are using a queue to throttle get thumbnail web service calls.
 * The queue is an array of callback functions to set AttachmentThumbnailState.
 * When the state is changed, the component will be rendered again.
 * We only allow certain concurrent requests to server. If more than max number,
 * the requests will be pushed to the queue. We are listening component's image load
 * and error events to process next request in queue.
 */
const throttledThumbnails: ((isThrottled: boolean) => void)[] = [];
const maxConcurrentRequests = 5;
let activeThumbnailRequests: number = 0;

/**
 * Most time get thumbnail web service call would be processed within certain time (5 seconds).
 * I have noticed that browsers may set requests to pending for long time and never process them.
 * Image load and error events are also not always triggered due to various reasons.
 * In case we ran into any issues, a watch dog timer will process the queue after certain time no action.
 */
const watchDogTimerInterval = 5000;
let watchDogTimer = null;
let processedRequests = 0;
let lastProcessedRequests = 0;

export function displayNextThumbnail() {
    processedRequests++;

    if (throttledThumbnails.length === 0) {
        // The queue is empty
        if (activeThumbnailRequests > 0) {
            activeThumbnailRequests--;
        }

        traceInfo('Queue is empty.');
    } else {
        // Get first callback from queue, and set isThrottled to false.
        // This callback will set component's state, and trigger the render function.
        const throttleCallback: (isThrottled: boolean) => void = throttledThumbnails.shift();
        throttleCallback(false /* isThrottled */);

        traceInfo('Display next.');
    }
}

export function registerCallback(throttleCallback: (isThrottled: boolean) => void) {
    if (throttleCallback == null) {
        throw new Error('A null callback is registered to Throttle Thumbnails queue.');
    }

    if (activeThumbnailRequests < maxConcurrentRequests) {
        // display now
        throttleCallback(false /* isThrottled */);

        activeThumbnailRequests++;

        processedRequests++;

        traceInfo('Display now.');
    } else {
        // The active requests are more than max allowed concurrent requests.
        // Push the callback to queue, and set component's isThrottled state to true.
        throttleCallback(true /* isThrottled */);
        throttledThumbnails.push(throttleCallback);
        traceInfo('Pushs to queue.');

        if (!watchDogTimer) {
            watchDogTimer = window.setInterval(watchDogAction, watchDogTimerInterval);

            traceInfo('Start watch dog.');
        }
    }
}

export function unregisterCallback(throttleHandler: (isThrottled: boolean) => void) {
    const index = throttledThumbnails.indexOf(throttleHandler);
    throttledThumbnails.splice(index, 1);

    // If thumbnail components are removing from DOM, we may never receive image load or error events
    // that we are used to process the queue. So manually set active requests to 0 to avoid issues.
    activeThumbnailRequests = 0;

    traceInfo('unregisterComponent:' + index);
}

function watchDogAction() {
    if (throttledThumbnails.length > 0) {
        if (processedRequests === lastProcessedRequests) {
            // The queue has not been processed for 5 seconds, force to process items.
            activeThumbnailRequests = 0;

            traceInfo('Watch dog is processing the queue.');

            displayNextThumbnail();
        } else {
            traceInfo('Watch dog is watching.');

            lastProcessedRequests = processedRequests;
        }
    } else {
        // There is nothing in the queue, stop watch dog
        window.clearInterval(watchDogTimer);
        watchDogTimer = null;

        traceInfo('Stop watch dog.');
    }
}

function traceInfo(info: string) {
    trace.info(
        `Attachment:throttleThumbnails:active=${activeThumbnailRequests};throttled=${throttledThumbnails.length};processed=${processedRequests};lastProcessed=${lastProcessedRequests};info=${info}`
    );
}
